import React, { useMemo, useState } from 'react';
import { Download, QrCode, Link2, Copy, Check } from 'lucide-react';
import s from './adminStyles';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = 'https://casaothers.nl';

/**
 * Central place where all CasaOthers QR codes live.
 *
 * Pre-configured entries cover the "official" QRs we ship on flyers,
 * business cards, and printed materials. The custom generator at the
 * bottom lets an admin produce a branded QR for any other CasaOthers
 * URL without needing another feature request.
 *
 * Design note: QRs are generated server-side (see /api/qr) so we can
 * embed the CasaOthers logo consistently and cache the PNGs. The client
 * only holds the URL — no private info.
 */

const PRESETS = [
  {
    id: 'koopplanner',
    title: 'De Complete Koopplanner Spanje',
    description: 'QR voor onze lead-magnet. Print op flyers, sluit aan bij events, of gebruik in blog artikelen.',
    url: `${SITE_URL}/gratis-koopplanner-spanje`,
    filename: 'casaothers-koopplanner-spanje.png',
  },
  {
    id: 'homepage',
    title: 'CasaOthers Homepage',
    description: 'Algemene site-QR — voor business cards en algemene marketing.',
    url: `${SITE_URL}/`,
    filename: 'casaothers-homepage.png',
  },
];

const QRCodesPanel = () => {
  const [customUrl, setCustomUrl] = useState('');
  const [customPreview, setCustomPreview] = useState(null);
  const [copied, setCopied] = useState(null);

  const previewCustom = () => {
    const trimmed = customUrl.trim();
    if (!trimmed) return;
    setCustomPreview(trimmed);
  };

  const copy = async (url, id) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(id);
      setTimeout(() => setCopied(null), 1500);
    } catch { /* clipboard blocked — silent */ }
  };

  return (
    <div data-testid="qr-panel">
      <div style={styles.headerRow}>
        <div>
          <h2 style={s.sectionTitle}>QR codes</h2>
          <p style={styles.subtitle}>
            Voor-geconfigureerde QR codes voor CasaOthers-materialen. Elk QR heeft ons palm-logo
            in het midden — direct herkenbaar als &quot;onze&quot; code. Download als PNG (op transparante
            achtergrond te printen) en gebruik op flyers, business cards, of digitaal.
          </p>
        </div>
      </div>

      <div style={styles.presetGrid}>
        {PRESETS.map(preset => (
          <QRCard
            key={preset.id}
            preset={preset}
            copied={copied === preset.id}
            onCopy={() => copy(preset.url, preset.id)}
          />
        ))}
      </div>

      <div style={styles.customCard} data-testid="qr-custom-generator">
        <h3 style={styles.cardTitle}>
          <QrCode size={18} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
          Eigen QR code maken
        </h3>
        <p style={styles.helpText}>
          Alleen CasaOthers-URLs (casaothers.nl of relatief pad). Voor externe URLs (bv. YouTube-video)
          gebruik <code>&amp;allow_external=true</code> aan het einde van de bovenstaande URL.
        </p>
        <div style={styles.inputRow}>
          <input
            style={s.input}
            type="url"
            value={customUrl}
            onChange={e => setCustomUrl(e.target.value)}
            placeholder="https://casaothers.nl/mijn-pagina"
            data-testid="qr-custom-url"
          />
          <button
            onClick={previewCustom}
            style={s.btnPrimary}
            disabled={!customUrl.trim()}
            data-testid="qr-custom-generate"
          >
            <QrCode size={16} /> Genereer
          </button>
        </div>
        {customPreview && (
          <QRCard
            preset={{
              id: 'custom',
              title: 'Aangepaste QR',
              description: customPreview,
              url: customPreview,
              filename: 'casaothers-custom-qr.png',
            }}
            copied={copied === 'custom'}
            onCopy={() => copy(customPreview, 'custom')}
          />
        )}
      </div>
    </div>
  );
};

const QRCard = ({ preset, copied, onCopy }) => {
  const qrUrl = useMemo(() => {
    const params = new URLSearchParams({ url: preset.url, size: '800' });
    return `${API_URL}/api/qr?${params}`;
  }, [preset.url]);
  const downloadUrl = qrUrl; // FastAPI already sends Content-Disposition inline
  return (
    <div style={styles.card} data-testid={`qr-card-${preset.id}`}>
      <div style={styles.qrImageWrap}>
        <img src={qrUrl} alt={`QR voor ${preset.title}`} style={styles.qrImage} loading="lazy" />
      </div>
      <div style={styles.cardBody}>
        <h3 style={styles.cardTitle}>{preset.title}</h3>
        <p style={styles.helpText}>{preset.description}</p>
        <div style={styles.urlRow}>
          <Link2 size={13} color="#8C8B88" />
          <span style={styles.url} title={preset.url}>{preset.url}</span>
        </div>
        <div style={styles.actions}>
          <a
            href={downloadUrl}
            download={preset.filename}
            style={{ ...s.btnPrimary, textDecoration: 'none' }}
            data-testid={`qr-download-${preset.id}`}
          >
            <Download size={16} /> Download PNG
          </a>
          <button onClick={onCopy} style={s.btnSecondary} data-testid={`qr-copy-${preset.id}`}>
            {copied ? <><Check size={16} /> Gekopieerd</> : <><Copy size={16} /> Kopieer URL</>}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  headerRow: { marginBottom: '1.5rem' },
  subtitle: { color: '#8C8B88', fontSize: '0.88rem', margin: '0.5rem 0 0', maxWidth: '720px', lineHeight: 1.6 },
  presetGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
    gap: '1.25rem',
    marginBottom: '2rem',
  },
  card: {
    display: 'flex',
    gap: '1.25rem',
    padding: '1.25rem',
    backgroundColor: 'white',
    border: '1px solid #EAE5DD',
    marginTop: '1.25rem',
  },
  qrImageWrap: {
    flexShrink: 0,
    width: '180px', height: '180px',
    backgroundColor: '#FAF9F6',
    border: '1px solid #EAE5DD',
    padding: '0.5rem',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  qrImage: { width: '100%', height: '100%', objectFit: 'contain' },
  cardBody: { flex: 1, display: 'flex', flexDirection: 'column' },
  cardTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.1rem', color: '#1A1918',
    margin: '0 0 0.5rem',
  },
  helpText: { color: '#585755', fontSize: '0.88rem', lineHeight: 1.55, margin: '0 0 0.75rem' },
  urlRow: {
    display: 'flex', alignItems: 'center', gap: '0.4rem',
    padding: '0.5rem 0.75rem', backgroundColor: '#FAF9F6',
    border: '1px solid #EAE5DD',
    fontSize: '0.8rem', color: '#1A1918',
    marginBottom: '0.75rem', overflow: 'hidden',
  },
  url: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  actions: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: 'auto' },
  customCard: {
    padding: '1.5rem',
    backgroundColor: '#FAF9F6',
    border: '1px solid #EAE5DD',
  },
  inputRow: { display: 'flex', gap: '0.5rem', alignItems: 'stretch', flexWrap: 'wrap' },
};

export default QRCodesPanel;
